import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Eye, History, X, FileDown, Mail, Search, ArrowUpDown, FilterX, Check, FileSpreadsheet, FileArchive, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Canonical manager_status badge config. Order reflects the natural B2B flow.
const STATUS_CONFIG = {
  ORDER_CREATED:   { labelKey: 'admin.orders_status.ORDER_CREATED',   fallback: 'Objednávka vytvorená',  cls: 'bg-slate-200 text-slate-800' },
  PROFORMA_ISSUED: { labelKey: 'admin.orders_status.PROFORMA_ISSUED', fallback: 'Záloh. faktúra vystavená', cls: 'bg-amber-200 text-amber-900' },
  PAID:            { labelKey: 'admin.orders_status.PAID',            fallback: 'Uhradené',     cls: 'bg-emerald-200 text-emerald-900' },
  INVOICED:        { labelKey: 'admin.orders_status.INVOICED',        fallback: 'Fakturované',  cls: 'bg-blue-200 text-blue-900' },
  SHIPPED:         { labelKey: 'admin.orders_status.SHIPPED',         fallback: 'Odoslané',     cls: 'bg-violet-200 text-violet-900' },
  DELIVERED:       { labelKey: 'admin.orders_status.DELIVERED',       fallback: 'Doručené',     cls: 'bg-green-200 text-green-900' },
  EXPIRED:         { labelKey: 'admin.orders_status.EXPIRED',         fallback: 'Exspirované',  cls: 'bg-gray-300 text-gray-800' },
  CANCELLED:       { labelKey: 'admin.orders_status.CANCELLED',       fallback: 'Zrušené',      cls: 'bg-red-200 text-red-900' },
};

const FILTER_OPTIONS = [
  { value: 'all',             labelKey: 'admin.orders_filter.all',             fallback: 'Všetky' },
  { value: 'ORDER_CREATED',   labelKey: 'admin.orders_filter.ORDER_CREATED',   fallback: 'Nové' },
  { value: 'PROFORMA_ISSUED', labelKey: 'admin.orders_filter.PROFORMA_ISSUED', fallback: 'Čakajú na úhradu' },
  { value: 'PAID',            labelKey: 'admin.orders_filter.PAID',            fallback: 'Uhradené' },
  { value: 'INVOICED',        labelKey: 'admin.orders_filter.INVOICED',        fallback: 'Fakturované' },
  { value: 'SHIPPED',         labelKey: 'admin.orders_filter.SHIPPED',         fallback: 'Odoslané' },
  { value: 'DELIVERED',       labelKey: 'admin.orders_filter.DELIVERED',       fallback: 'Doručené' },
  { value: 'EXPIRED',         labelKey: 'admin.orders_filter.EXPIRED',         fallback: 'Exspirované' },
  { value: 'CANCELLED',       labelKey: 'admin.orders_filter.CANCELLED',       fallback: 'Zrušené' },
];

// For each current state, which actions does the admin have? Mirrors
// order_state.ALLOWED_TRANSITIONS on the backend so buttons never ask for an
// impossible transition.
const ACTIONS_BY_STATE = {
  ORDER_CREATED: [
    { action: 'issue-proforma', labelKey: 'admin.orders_action.issue_proforma', fallback: 'Vystaviť záloh. faktúru', variant: 'default' },
    { action: 'cancel',         labelKey: 'admin.orders_action.cancel',         fallback: 'Zrušiť', variant: 'outline' },
  ],
  PROFORMA_ISSUED: [
    { action: 'mark-paid', labelKey: 'admin.orders_action.mark_paid', fallback: 'Označiť ako zaplatené', variant: 'default' },
    { action: 'cancel',    labelKey: 'admin.orders_action.cancel',    fallback: 'Zrušiť', variant: 'outline' },
  ],
  PAID: [
    { action: 'issue-invoice', labelKey: 'admin.orders_action.issue_invoice', fallback: 'Vystaviť faktúru', variant: 'default' },
    { action: 'cancel',        labelKey: 'admin.orders_action.cancel',        fallback: 'Zrušiť', variant: 'outline' },
  ],
  INVOICED: [
    { action: 'mark-shipped', labelKey: 'admin.orders_action.mark_shipped', fallback: 'Odoslať', variant: 'default', promptTracking: true },
    { action: 'cancel',       labelKey: 'admin.orders_action.cancel',       fallback: 'Zrušiť', variant: 'outline' },
  ],
  SHIPPED: [
    { action: 'mark-delivered', labelKey: 'admin.orders_action.mark_delivered', fallback: 'Označiť ako doručené', variant: 'default' },
  ],
  DELIVERED: [],
  EXPIRED: [],
  CANCELLED: [],
};

// Legacy fallback used only for orders created before Phase 2a had
// manager_status. Infers something sensible from payment_status/order_status
// so the UI is never blank.
const inferManagerStatus = (order) => {
  if (order.manager_status) return order.manager_status;
  const os = (order.order_status || '').toLowerCase();
  const ps = (order.payment_status || '').toLowerCase();
  if (os === 'delivered') return 'DELIVERED';
  if (os === 'shipped') return 'SHIPPED';
  if (os === 'cancelled' || os === 'canceled') return 'CANCELLED';
  if (ps === 'paid') return 'PAID';
  return 'ORDER_CREATED';
};

const StatusBadge = ({ status, testId }) => {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG[status];
  const label = cfg
    ? t(cfg.labelKey, cfg.fallback)
    : (status || '—');
  const cls = cfg?.cls || 'bg-gray-100 text-gray-700';
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}
    >
      {label}
    </span>
  );
};

const I18N_TO_LOCALE = { sk: 'sk-SK', cs: 'cs-CZ', de: 'de-DE', en: 'en-US', ru: 'ru-RU' };

const formatDate = (iso, locale = 'sk-SK') => {
  if (!iso) return '';
  return new Date(iso).toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const AdminOrders = () => {
  const { t, i18n } = useTranslation();
  const locale = I18N_TO_LOCALE[(i18n.language || 'sk').split('-')[0].toLowerCase()] || 'sk-SK';
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_desc');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [stateLog, setStateLog] = useState(null); // { orderId, logs }
  const [documents, setDocuments] = useState({}); // { orderId: [docs] }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Backend only supports ?status=<order_status>. We pull everything and
      // filter client-side by manager_status.
      const resp = await fetch(`${API_URL}/api/admin/orders?limit=500`, {
        headers: authHeaders,
      });
      if (!resp.ok) throw new Error('Fetch failed');
      const data = await resp.json();
      setOrders(data.orders || []);
    } catch (e) {
      console.error('Orders fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filteredOrders = useMemo(() => {
    let result = filter === 'all' ? orders : orders.filter(o => inferManagerStatus(o) === filter);

    // Free-text search across id, customer name, email, order_id (full)
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(o => (
        (o.id || '').toLowerCase().includes(q) ||
        (o.customer_name || '').toLowerCase().includes(q) ||
        (o.customer_email || '').toLowerCase().includes(q) ||
        (o.order_id || '').toLowerCase().includes(q)
      ));
    }

    // Date range — based on order's created_at (ISO)
    if (dateFrom) {
      const t = new Date(dateFrom + 'T00:00:00').getTime();
      result = result.filter(o => new Date(o.created_at).getTime() >= t);
    }
    if (dateTo) {
      const t = new Date(dateTo + 'T23:59:59').getTime();
      result = result.filter(o => new Date(o.created_at).getTime() <= t);
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'created_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'amount_desc':
          return (b.total_eur || 0) - (a.total_eur || 0);
        case 'amount_asc':
          return (a.total_eur || 0) - (b.total_eur || 0);
        case 'created_desc':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    return result;
  }, [orders, filter, search, dateFrom, dateTo, sortBy]);

  const clearFilters = () => {
    setFilter('all');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setSortBy('created_desc');
  };
  const hasActiveFilters = filter !== 'all' || search || dateFrom || dateTo || sortBy !== 'created_desc';

  const runAction = async (order, actionDef) => {
    if (actionBusy) return;
    let body = null;
    if (actionDef.promptTracking) {
      const tracking = window.prompt(t('admin.orders.tracking_prompt', 'Tracking number (voliteľné):'), '');
      if (tracking === null) return; // cancelled
      if (tracking.trim()) body = { tracking_number: tracking.trim() };
    } else if (actionDef.action === 'cancel') {
      if (!window.confirm(t('admin.orders.cancel_confirm', 'Naozaj zrušiť objednávku? Rezervácia bude uvoľnená.'))) return;
    }
    setActionBusy(order.id + ':' + actionDef.action);
    try {
      const resp = await fetch(
        `${API_URL}/api/admin/orders/${order.id}/${actionDef.action}`,
        {
          method: 'POST',
          headers: authHeaders,
          body: body ? JSON.stringify(body) : undefined,
        },
      );
      const data = await resp.json();
      if (!resp.ok) {
        alert(t('admin.orders.error_prefix', 'Chyba') + ': ' + (data.detail || resp.statusText));
      } else {
        // Optimistic: replace the one changed order in state so the table
        // updates without a full refetch. Also updates modal if open.
        const updated = data.order || {};
        setOrders(prev => prev.map(o => (o.id === order.id ? { ...o, ...updated } : o)));
        if (selectedOrder?.id === order.id) {
          setSelectedOrder({ ...selectedOrder, ...updated });
        }
      }
    } catch (e) {
      alert(t('admin.orders.network_error', 'Sieťová chyba') + ': ' + e.message);
    } finally {
      setActionBusy(null);
    }
  };

  const openStateLog = async (orderId) => {
    try {
      const resp = await fetch(
        `${API_URL}/api/admin/orders/${orderId}/state-log`,
        { headers: authHeaders },
      );
      if (!resp.ok) throw new Error('Log fetch failed');
      const data = await resp.json();
      setStateLog({ orderId, logs: data.logs || [] });
    } catch (e) {
      alert(t('admin.orders.history_error', 'Chyba pri načítaní histórie') + ': ' + e.message);
    }
  };

  const runBulkMarkPaid = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(t('admin.orders.bulk_mark_paid_confirm', 'Naozaj označiť {{count}} objednávok ako zaplatené?', { count: ids.length }))) return;
    setBulkBusy(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/orders/bulk-mark-paid`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ order_ids: ids }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || t('admin.orders.bulk_error', 'Chyba bulk operácie'));
        return;
      }
      const failedDetail = (data.failures || []).slice(0, 3)
        .map((f) => `#${(f.order_id || '').slice(0,8)}: ${f.reason}`).join(' · ');
      toast.success(
        t('admin.orders.bulk_marked_summary', 'Označené: {{updated}}, chýb: {{failed}}', { updated: data.updated, failed: data.failed }) + (failedDetail ? ` (${failedDetail})` : ''),
      );
      setSelectedIds(new Set());
      await fetchOrders();
    } catch (e) {
      toast.error(t('admin.orders.bulk_error', 'Chyba bulk operácie'));
    } finally {
      setBulkBusy(false);
    }
  };

  const _downloadBlob = async (path) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return null;
    setBulkBusy(true);
    try {
      const resp = await fetch(`${API_URL}${path}`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ order_ids: ids }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || t('admin.orders.export_error', 'Chyba exportu'));
        return null;
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('content-disposition') || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m ? m[1] : 'orders-export';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      return filename;
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkExportCsv = async () => {
    const f = await _downloadBlob('/api/admin/orders/bulk-export-csv');
    if (f) toast.success(t('admin.orders.csv_ready', 'CSV pripravené: {{filename}}', { filename: f }));
  };

  const runBulkExportPdfs = async () => {
    const f = await _downloadBlob('/api/admin/orders/bulk-export-pdfs');
    if (f) toast.success(t('admin.orders.zip_ready', 'ZIP pripravené: {{filename}}', { filename: f }));
  };

  const loadDocuments = useCallback(async (orderId) => {
    try {
      const resp = await fetch(
        `${API_URL}/api/admin/orders/${orderId}/documents`,
        { headers: authHeaders },
      );
      if (!resp.ok) return;
      const data = await resp.json();
      setDocuments(prev => ({ ...prev, [orderId]: data.documents || [] }));
    } catch (e) {
      console.error('Documents load failed:', e);
    }
  }, [authHeaders]);

  // When a detail modal opens, preload its documents so the "Dokumenty"
  // section renders without a second click.
  useEffect(() => {
    if (selectedOrder?.id) loadDocuments(selectedOrder.id);
  }, [selectedOrder?.id, loadDocuments]);

  const downloadDocument = async (docId, filename) => {
    try {
      const resp = await fetch(
        `${API_URL}/api/admin/documents/${docId}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(t('admin.orders.download_error', 'Chyba pri sťahovaní') + ': ' + e.message);
    }
  };

  const emailDocument = async (docId) => {
    if (!window.confirm(t('admin.orders.email_doc_confirm', 'Odoslať doklad na email zákazníka?'))) return;
    try {
      const resp = await fetch(
        `${API_URL}/api/admin/documents/${docId}/email`,
        { method: 'POST', headers: authHeaders },
      );
      const data = await resp.json();
      if (!resp.ok) {
        alert(t('admin.orders.error_prefix', 'Chyba') + ': ' + (data.detail || resp.statusText));
      } else {
        alert(t('admin.orders.sent_to', 'Odoslané na') + ': ' + data.sent_to);
        if (selectedOrder?.id) loadDocuments(selectedOrder.id);
      }
    } catch (e) {
      alert(t('admin.orders.network_error', 'Sieťová chyba') + ': ' + e.message);
    }
  };

  const backfillDocuments = async (orderId) => {
    try {
      const resp = await fetch(
        `${API_URL}/api/admin/orders/${orderId}/backfill-documents`,
        { method: 'POST', headers: authHeaders },
      );
      const data = await resp.json();
      if (!resp.ok) {
        alert(t('admin.orders.error_prefix', 'Chyba') + ': ' + (data.detail || resp.statusText));
      } else {
        const count = (data.backfilled || []).length;
        alert(count
          ? t('admin.orders.backfill_created', 'Vytvorené: {{count}} doklad(y)', { count })
          : t('admin.orders.backfill_nothing', 'Nie je čo doplniť'));
        loadDocuments(orderId);
      }
    } catch (e) {
      alert(t('admin.orders.network_error', 'Sieťová chyba') + ': ' + e.message);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6" data-testid="admin-orders-title">{t('admin.orders.title', 'Objednávky')}</h1>

      {/* Search + date range + sort + clear */}
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('admin.orders.search_placeholder', 'Hľadať podľa mena, emailu, ID objednávky…')}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
              data-testid="orders-search-input"
            />
          </div>
          <div className="md:col-span-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="Od"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              data-testid="orders-date-from"
            />
          </div>
          <div className="md:col-span-2">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="Do"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              data-testid="orders-date-to"
            />
          </div>
          <div className="md:col-span-2 relative">
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              data-testid="orders-sort-select"
            >
              <option value="created_desc">{t('admin.orders.sort_newest', 'Najnovšie')}</option>
              <option value="created_asc">{t('admin.orders.sort_oldest', 'Najstaršie')}</option>
              <option value="amount_desc">{t('admin.orders.sort_amount_desc', 'Najvyššia suma')}</option>
              <option value="amount_asc">{t('admin.orders.sort_amount_asc', 'Najnižšia suma')}</option>
            </select>
          </div>
          {hasActiveFilters && (
            <div className="md:col-span-1 flex items-center justify-center">
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md w-full justify-center"
                title={t('admin.orders.clear_filters', 'Vyčistiť filtre')}
                data-testid="orders-clear-filters"
              >
                <FilterX className="h-4 w-4" />
                <span className="md:hidden">{t('admin.orders.clear', 'Vyčistiť')}</span>
              </button>
            </div>
          )}
        </div>
        <div className="mt-3 text-xs text-gray-500" data-testid="orders-count">
          {t('admin.orders.shown_of', 'Zobrazených')}: <strong>{filteredOrders.length}</strong> {t('admin.orders.of_total', 'z')} {orders.length}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.map(opt => {
            const count = opt.value === 'all'
              ? orders.length
              : orders.filter(o => inferManagerStatus(o) === opt.value).length;
            return (
              <button
                key={opt.value}
                data-testid={`filter-${opt.value}`}
                onClick={() => setFilter(opt.value)}
                className={`px-4 py-2 rounded-full text-sm transition ${
                  filter === opt.value
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t(opt.labelKey, opt.fallback)} <span className="opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bulk actions toolbar (visible when ≥1 selected) */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2" data-testid="orders-bulk-toolbar">
          <span className="text-sm font-medium text-orange-900" data-testid="orders-bulk-count">
            {t('admin.orders.bulk_selected', '{{count}} vybraných', { count: selectedIds.size })}
          </span>
          <Button
            size="sm"
            disabled={bulkBusy}
            onClick={runBulkMarkPaid}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="orders-bulk-mark-paid"
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
            {t('admin.orders_action.mark_paid', 'Označiť ako zaplatené')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={bulkBusy}
            onClick={runBulkExportCsv}
            data-testid="orders-bulk-export-csv"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1" />
            {t('admin.orders.export_csv', 'Export CSV')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={bulkBusy}
            onClick={runBulkExportPdfs}
            data-testid="orders-bulk-export-pdfs"
          >
            <FileArchive className="h-4 w-4 mr-1" />
            {t('admin.orders.export_pdfs', 'Export PDF (ZIP)')}
          </Button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
            data-testid="orders-bulk-clear"
          >
            {t('admin.orders.bulk_clear', 'Zrušiť výber')}
          </button>
        </div>
      )}

      {/* Orders table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="orders-empty">{t('admin.orders.empty', 'Žiadne objednávky')}</div>
        ) : (
          <>
          {/* Mobile card layout (<md) */}
          <div className="md:hidden divide-y divide-gray-200">
            {filteredOrders.map(order => {
              const ms = inferManagerStatus(order);
              const actions = ACTIONS_BY_STATE[ms] || [];
              return (
                <div
                  key={order.id}
                  className="p-4 space-y-3"
                  data-testid={`order-card-${order.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-gray-500">#{order.id?.slice(0, 8)}</div>
                      <div className="font-semibold text-gray-900 truncate">{order.customer_name}</div>
                      <div className="text-xs text-gray-500 truncate">{order.customer_email}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-bold text-gray-900">€{order.total_eur?.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">{formatDate(order.created_at, locale)}</div>
                    </div>
                  </div>
                  <StatusBadge status={ms} testId={`status-${order.id}`} />
                  <div className="flex flex-wrap gap-2">
                    {actions.map(a => (
                      <Button
                        key={a.action}
                        size="sm"
                        variant={a.variant}
                        disabled={actionBusy === order.id + ':' + a.action}
                        onClick={() => runAction(order, a)}
                        data-testid={`action-${a.action}-${order.id}-mobile`}
                        className={a.variant === 'default' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
                      >
                        {a.label}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedOrder(order)}
                      data-testid={`action-view-${order.id}-mobile`}
                    >
                      <Eye className="h-4 w-4 mr-1" /> {t('admin.orders.detail', 'Detail')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openStateLog(order.id)}
                      data-testid={`action-log-${order.id}-mobile`}
                    >
                      <History className="h-4 w-4 mr-1" /> {t('admin.orders.history', 'História')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table (>=md) */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filteredOrders.length > 0 && filteredOrders.every((o) => selectedIds.has(o.id))}
                      onChange={(e) => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) filteredOrders.forEach((o) => next.add(o.id));
                        else filteredOrders.forEach((o) => next.delete(o.id));
                        setSelectedIds(next);
                      }}
                      className="rounded"
                      data-testid="orders-bulk-select-all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.orders.col_id', 'ID')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.orders.col_customer', 'Zákazník')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.orders.col_amount', 'Suma')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.orders.col_status', 'Status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.orders.col_date', 'Dátum')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('admin.orders.col_actions', 'Akcie')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map(order => {
                  const ms = inferManagerStatus(order);
                  const actions = ACTIONS_BY_STATE[ms] || [];
                  return (
                    <tr key={order.id} className="hover:bg-gray-50" data-testid={`order-row-${order.id}`}>
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(order.id)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(order.id);
                            else next.delete(order.id);
                            setSelectedIds(next);
                          }}
                          className="rounded"
                          data-testid={`orders-bulk-select-${order.id}`}
                        />
                      </td>
                      <td className="px-4 py-4 font-mono text-sm">#{order.id?.slice(0, 8)}</td>
                      <td className="px-4 py-4">
                        <div className="font-medium">{order.customer_name}</div>
                        <div className="text-xs text-gray-500">{order.customer_email}</div>
                      </td>
                      <td className="px-4 py-4 font-medium whitespace-nowrap">€{order.total_eur?.toFixed(2)}</td>
                      <td className="px-4 py-4">
                        <StatusBadge status={ms} testId={`status-${order.id}`} />
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(order.created_at, locale)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          {actions.map(a => (
                            <Button
                              key={a.action}
                              size="sm"
                              variant={a.variant}
                              disabled={actionBusy === order.id + ':' + a.action}
                              onClick={() => runAction(order, a)}
                              data-testid={`action-${a.action}-${order.id}`}
                              className={a.variant === 'default' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
                            >
                              {t(a.labelKey, a.fallback)}
                            </Button>
                          ))}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedOrder(order)}
                            data-testid={`action-view-${order.id}`}
                            title={t('admin.orders.detail', 'Detail')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openStateLog(order.id)}
                            data-testid={`action-log-${order.id}`}
                            title={t('admin.orders.history', 'História')}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Detail modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{t('admin.orders.detail_title', 'Objednávka')} #{selectedOrder.id?.slice(0, 8)}</h2>
                <div className="mt-1">
                  <StatusBadge status={inferManagerStatus(selectedOrder)} />
                </div>
              </div>
              <button onClick={() => setSelectedOrder(null)} data-testid="modal-close">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold mb-2">{t('admin.orders.customer', 'Zákazník')}</h3>
                <p>{selectedOrder.customer_name}</p>
                <p className="text-gray-500">{selectedOrder.customer_email}</p>
                <p className="text-gray-500">{selectedOrder.customer_phone}</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">{t('admin.orders.delivery_address', 'Adresa doručenia')}</h3>
                <p>{selectedOrder.delivery_address?.street}</p>
                <p>
                  {selectedOrder.delivery_address?.city}, {selectedOrder.delivery_address?.postal_code}
                </p>
                <p>{selectedOrder.delivery_address?.country}</p>
                {selectedOrder.delivery_address?.tax_id && (
                  <p className="text-sm text-gray-500 mt-1">
                    VAT ID: {selectedOrder.delivery_address.tax_id}
                    {selectedOrder.reverse_charge && (
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                        {t('admin.orders.reverse_charge', 'Reverse charge (VIES overené)')}
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div>
                <h3 className="font-semibold mb-2">{t('admin.orders.items', 'Položky')}</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  {selectedOrder.items?.map((item, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{item.product_name} × {item.quantity} {item.unit || 'mb'}</span>
                      <span>€{(item.price_eur * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 font-bold flex justify-between">
                    <span>{t('admin.orders.total', 'Celkom')}</span>
                    <span>€{selectedOrder.total_eur?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {selectedOrder.tracking_number && (
                <div>
                  <h3 className="font-semibold mb-1">{t('admin.orders.tracking', 'Tracking')}</h3>
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">{selectedOrder.tracking_number}</code>
                </div>
              )}

              {selectedOrder.proforma_issued_at && (
                <div className="text-sm text-gray-500">
                  {t('admin.orders.proforma_issued_at', 'Záloh. faktúra vystavená')}: {formatDate(selectedOrder.proforma_issued_at, locale)}
                </div>
              )}

              {/* Documents (proforma, final invoice). Shown for any order
                  past ORDER_CREATED — if documents are missing (legacy orders
                  that transitioned before Phase 3 was deployed) the admin
                  can backfill them. */}
              {['PROFORMA_ISSUED','PAID','INVOICED','SHIPPED','DELIVERED'].includes(inferManagerStatus(selectedOrder)) && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{t('admin.orders.documents', 'Dokumenty')}</h3>
                    {(documents[selectedOrder.id] || []).length === 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => backfillDocuments(selectedOrder.id)}
                        data-testid="backfill-docs"
                      >
                        {t('admin.orders.backfill_docs', 'Vygenerovať chýbajúce dokumenty')}
                      </Button>
                    )}
                  </div>
                  {(documents[selectedOrder.id] || []).length === 0 ? (
                    <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                      {t('admin.orders.no_documents', 'Pre túto objednávku nie sú evidované žiadne doklady.')}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(documents[selectedOrder.id] || []).map(d => (
                        <div
                          key={d.id}
                          className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                          data-testid={`document-${d.id}`}
                        >
                          <div className="flex-1">
                            <div className="font-semibold">
                              {d.type === 'proforma' ? t('admin.orders.doc_proforma', 'Zálohová faktúra')
                                : d.type === 'delivery_note' ? t('admin.orders.doc_delivery_note', 'Dodací list')
                                : t('admin.orders.doc_invoice', 'Faktúra')}
                              {' '}
                              <span className="text-gray-500 font-normal">{d.number}</span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(d.issued_at, locale)}
                              {d.type !== 'delivery_note' && (
                                <> · {d.total?.toFixed(2)} {d.currency || 'EUR'}</>
                              )}
                              {d.email_sent_at && (
                                <span className="ml-2 text-emerald-600">
                                  · {t('admin.orders.email_sent', 'Email odoslaný')} {formatDate(d.email_sent_at, locale)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadDocument(d.id, `${d.number}.pdf`)}
                              data-testid={`doc-download-${d.id}`}
                            >
                              <FileDown className="h-4 w-4 mr-1" />
                              PDF
                            </Button>
                            {d.type !== 'delivery_note' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => emailDocument(d.id)}
                                data-testid={`doc-email-${d.id}`}
                              >
                                <Mail className="h-4 w-4 mr-1" />
                                Email
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* In-modal quick actions — same set as inline row buttons */}
              <div className="pt-2 border-t">
                <h3 className="font-semibold mb-2">{t('admin.orders.next_step', 'Ďalší krok')}</h3>
                <div className="flex flex-wrap gap-2">
                  {(ACTIONS_BY_STATE[inferManagerStatus(selectedOrder)] || []).map(a => (
                    <Button
                      key={a.action}
                      size="sm"
                      variant={a.variant}
                      disabled={actionBusy === selectedOrder.id + ':' + a.action}
                      onClick={() => runAction(selectedOrder, a)}
                      className={a.variant === 'default' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
                    >
                      {t(a.labelKey, a.fallback)}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openStateLog(selectedOrder.id)}
                  >
                    <History className="h-4 w-4 mr-1" />
                    {t('admin.orders.history', 'História')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* State log modal */}
      {stateLog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto" data-testid="state-log-modal">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">{t('admin.orders.state_log_title', 'História zmien')}</h3>
              <button onClick={() => setStateLog(null)} data-testid="log-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              {stateLog.logs.length === 0 ? (
                <div className="text-gray-500 text-sm">{t('admin.orders.no_state_changes', 'Žiadne zmeny.')}</div>
              ) : (
                stateLog.logs.map((l, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div>
                        <StatusBadge status={l.from_state} /> → <StatusBadge status={l.to_state} />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatDate(l.created_at, locale)} · {l.actor_email || '—'}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;

